import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  LinearProgress,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SmsIcon from '@mui/icons-material/Sms';
import { trpc } from '../../lib/trpc';
import { ChannelCard, ChannelCardSkeleton } from '../../components/notifications';
import type { ChannelType } from '../../components/notifications';

/**
 * Notification Channels list page component
 * Displays all notification channels with toggle, test, edit, and delete actions
 */
export function NotificationChannelsPage() {
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState<string | null>(null);

  // Test result tracking
  const [testResults, setTestResults] = useState<
    Record<string, { success: boolean; error?: string }>
  >({});
  const [testingChannel, setTestingChannel] = useState<string | null>(null);

  // Resend verification tracking
  const [resendingVerificationChannel, setResendingVerificationChannel] = useState<string | null>(null);

  // Snackbar for feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch channels
  const {
    data: channels,
    isLoading,
    error,
    refetch,
    isFetching,
  } = trpc.notifications.list.useQuery();

  // Fetch limit info
  const { data: limitInfo } = trpc.notifications.limits.useQuery();

  // Fetch SMS usage info
  const { data: smsUsage } = trpc.notifications.smsUsage.useQuery();

  // Mutations
  const toggleMutation = trpc.notifications.toggle.useMutation({
    onSuccess: (data) => {
      refetch();
      setSnackbar({
        open: true,
        message: `Channel ${data.enabled ? 'enabled' : 'disabled'} successfully`,
        severity: 'success',
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to toggle channel',
        severity: 'error',
      });
    },
  });

  const testMutation = trpc.notifications.test.useMutation({
    onSuccess: (data, variables) => {
      const errorMsg = data.error ?? undefined;
      setTestResults((prev) => ({
        ...prev,
        [variables.id]: { success: data.success, ...(errorMsg ? { error: errorMsg } : {}) },
      }));
      setTestingChannel(null);
      setSnackbar({
        open: true,
        message: data.message,
        severity: data.success ? 'success' : 'error',
      });
    },
    onError: (error, variables) => {
      setTestResults((prev) => ({
        ...prev,
        [variables.id]: { success: false, error: error.message },
      }));
      setTestingChannel(null);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send test notification',
        severity: 'error',
      });
    },
  });

  const deleteMutation = trpc.notifications.delete.useMutation({
    onSuccess: () => {
      refetch();
      setDeleteDialogOpen(false);
      setChannelToDelete(null);
      setSnackbar({
        open: true,
        message: 'Channel deleted successfully',
        severity: 'success',
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete channel',
        severity: 'error',
      });
    },
  });

  const resendVerificationMutation = trpc.notifications.resendVerification.useMutation({
    onSuccess: (data) => {
      setResendingVerificationChannel(null);
      setSnackbar({
        open: true,
        message: data.message || 'Verification email sent successfully',
        severity: 'success',
      });
    },
    onError: (error) => {
      setResendingVerificationChannel(null);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send verification email',
        severity: 'error',
      });
    },
  });

  // Handlers
  const handleToggle = (id: string, enabled: boolean) => {
    toggleMutation.mutate({ id, enabled });
  };

  const handleTest = (id: string) => {
    setTestingChannel(id);
    testMutation.mutate({ id });
  };

  const handleDeleteClick = (id: string) => {
    setChannelToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (channelToDelete) {
      deleteMutation.mutate({ id: channelToDelete });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setChannelToDelete(null);
  };

  const handleResendVerification = (id: string) => {
    setResendingVerificationChannel(id);
    resendVerificationMutation.mutate({ channelId: id });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Check if can add more channels
  const canAddChannel = limitInfo ? limitInfo.current < limitInfo.limit : true;

  // Check if any email channels need verification
  const unverifiedEmailChannels = channels?.filter(
    (channel) => channel.type === 'EMAIL' && !channel.verified
  ) || [];

  // Check if user has any SMS channels
  const hasSmsChannels = channels?.some((channel) => channel.type === 'SMS') || false;

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Notification Channels
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure how you receive alerts about your monitors
            {limitInfo &&
              ` (${limitInfo.current}/${limitInfo.limit === Infinity ? 'unlimited' : limitInfo.limit})`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => refetch()}
              disabled={isFetching}
              sx={{ border: 1, borderColor: 'divider' }}
            >
              <RefreshIcon
                sx={{
                  animation: isFetching ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              !canAddChannel ? `You've reached your plan limit of ${limitInfo?.limit} channels` : ''
            }
          >
            <span>
              <Button
                component={RouterLink}
                to="/notifications/new"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!canAddChannel}
              >
                Add Channel
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error loading notification channels</AlertTitle>
          {error.message}
        </Alert>
      )}

      {/* Unverified Channels Warning */}
      {unverifiedEmailChannels.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Email verification required</AlertTitle>
          {unverifiedEmailChannels.length === 1
            ? `You have 1 email channel pending verification. Please check your inbox and verify your email address to receive notifications.`
            : `You have ${unverifiedEmailChannels.length} email channels pending verification. Please check your inbox and verify your email addresses to receive notifications.`}
        </Alert>
      )}

      {/* SMS Usage Info - Show if user has SMS channels */}
      {hasSmsChannels && smsUsage && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <SmsIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              SMS Usage This Month
            </Typography>
            <Chip
              label={`${smsUsage.used}/${smsUsage.limit}`}
              size="small"
              color={smsUsage.remaining === 0 ? 'error' : smsUsage.remaining <= 10 ? 'warning' : 'default'}
              variant="outlined"
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={(smsUsage.used / smsUsage.limit) * 100}
            color={smsUsage.remaining === 0 ? 'error' : smsUsage.remaining <= 10 ? 'warning' : 'primary'}
            sx={{ height: 8, borderRadius: 1, mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {smsUsage.remaining > 0
              ? `${smsUsage.remaining} SMS messages remaining this month. Limit resets on the 1st.`
              : 'Monthly SMS limit reached. Limit resets on the 1st of next month.'}
          </Typography>
        </Paper>
      )}

      {/* Content */}
      {isLoading ? (
        // Loading state
        <Grid container spacing={2}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <ChannelCardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : channels && channels.length === 0 ? (
        // Empty state
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <NotificationsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No notification channels yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Add a notification channel to receive alerts when your monitors go down.
          </Typography>
          <Button
            component={RouterLink}
            to="/notifications/new"
            variant="contained"
            startIcon={<AddIcon />}
          >
            Add Your First Channel
          </Button>
        </Paper>
      ) : (
        // Channels grid
        <Grid container spacing={2}>
          {channels?.map((channel) => (
            <Grid item xs={12} sm={6} lg={4} key={channel.id}>
              <ChannelCard
                channel={{
                  id: channel.id,
                  name: channel.name,
                  type: channel.type as ChannelType,
                  enabled: channel.enabled,
                  config: channel.config as {
                    email?: string;
                    url?: string;
                    webhookUrl?: string;
                    channel?: string;
                    method?: string;
                  },
                  verified: channel.verified,
                }}
                onToggle={handleToggle}
                onTest={handleTest}
                onDelete={handleDeleteClick}
                onResendVerification={handleResendVerification}
                isToggling={toggleMutation.isLoading}
                isTesting={testingChannel === channel.id}
                isResendingVerification={resendingVerificationChannel === channel.id}
                testResult={testResults[channel.id] ?? null}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Notification Channel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this notification channel? This action cannot be undone
            and you will no longer receive notifications through this channel.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default NotificationChannelsPage;
